import { useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Flag } from "@/components/Flag";

const RECENT_KEY = "chatmilu.lang.recent";
const KEEP = 5;

const recall = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").slice(0, KEEP);
  } catch {
    return [];
  }
};

// Language picker with flags — a drop-in replacement for a native <select>.
export const LanguageSelect = ({ languages, value, onChange, testId = "language-select",
                                compact = false, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState(recall);
  const current = languages.find((l) => l.code === value);
  const list = languages.filter(
    (l) =>
      l.name.toLowerCase().includes(q.toLowerCase()) ||
      l.native.toLowerCase().includes(q.toLowerCase())
  );
  // the five most recently chosen, kept at the top of the list
  const onTop = q ? [] : recent.map((code) => languages.find((l) => l.code === code)).filter(Boolean);

  const pick = (code) => {
    const next = [code, ...recent.filter((c) => c !== code)].slice(0, KEEP);
    setRecent(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    onChange(code);
    setOpen(false);
    setQ("");
  };

  const row = (l, where) => (
    <button
      key={`${where}-${l.code}`}
      data-testid={`${testId}-${where}-${l.code}`}
      onClick={() => pick(l.code)}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm transition-colors hover:bg-surfacealt"
    >
      <Flag code={l.code} testId={`${testId}-flag-${l.code}`} />
      <span className="min-w-0 flex-1">
        <span dir="auto" className="block truncate font-medium">{l.native}</span>
        <span className="block truncate text-xs text-ink-soft">{l.name}</span>
      </span>
      {l.code === value && <Check className="h-4 w-4 shrink-0 text-brand" />}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          disabled={disabled}
          className={
            compact
              ? "flex min-h-[36px] items-center gap-2 rounded-full border border-white/15 bg-panel px-3 text-sm transition-colors hover:border-brand/50 disabled:opacity-50"
              : "flex h-11 min-w-[10rem] flex-1 items-center gap-2.5 rounded-xl border border-white/15 bg-panel px-3 text-sm transition-colors hover:border-brand/50 disabled:opacity-50"
          }
        >
          <Flag code={current?.code || value} />
          <span dir="auto" className={compact ? "max-w-[8rem] truncate text-start" : "min-w-0 flex-1 truncate text-start"}>
            {current?.native || "English"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2" data-testid={`${testId}-panel`}>
        <div className="relative mb-2">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-soft" />
          <input
            data-testid={`${testId}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search 50 languages…"
            className="h-9 w-full rounded-xl border border-white/10 bg-page ps-9 pe-3 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
          />
        </div>
        <div className="cml-scroll max-h-64 overflow-y-auto">
          {onTop.length > 0 && (
            <div data-testid={`${testId}-recent`}>
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-ink-soft">
                Recently chosen
              </p>
              {onTop.map((l) => row(l, "recent"))}
              <div className="my-1.5 border-t border-white/10" />
            </div>
          )}
          {list.map((l) => row(l, "option"))}
          {list.length === 0 && <p className="p-3 text-sm text-ink-soft">No match</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
